import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import Layout from "../../layout/Layout";
import { getAylikKalipKullanimAnalizi } from "../../../api/uretim/kalipApi";

const MACHINE_OPTIONS = [
  { value: "all", label: "Tüm Makineler" },
  { value: "1", label: "Makina 1" },
  { value: "2", label: "Makina 2" },
  { value: "3", label: "Makina 3" },
  { value: "4", label: "Makina 4" },
];

const PRODUCT_TYPE_OPTIONS = [
  { value: "all", label: "Tüm Ürün Tipleri" },
  { value: "BORU", label: "Boru" },
  { value: "PROFİL", label: "Profil" },
];

// Her kalıp, aynı makine kartı içinde her zaman aynı rengi kullanır.
const KALIP_COLOR_PALETTE = [
  { background: "#DBEAFE", border: "#2563EB", text: "#1E3A8A" },
  { background: "#DCFCE7", border: "#16A34A", text: "#14532D" },
  { background: "#FEF3C7", border: "#D97706", text: "#78350F" },
  { background: "#FCE7F3", border: "#DB2777", text: "#831843" },
  { background: "#EDE9FE", border: "#7C3AED", text: "#4C1D95" },
  { background: "#CFFAFE", border: "#0891B2", text: "#164E63" },
  { background: "#FFEDD5", border: "#EA580C", text: "#7C2D12" },
  { background: "#FEE2E2", border: "#DC2626", text: "#7F1D1D" },
  { background: "#ECFCCB", border: "#65A30D", text: "#365314" },
  { background: "#E0E7FF", border: "#4F46E5", text: "#312E81" },
  { background: "#CCFBF1", border: "#0D9488", text: "#134E4A" },
  { background: "#FAE8FF", border: "#C026D3", text: "#701A75" },
  { background: "#F3E8FF", border: "#9333EA", text: "#581C87" },
  { background: "#FFE4E6", border: "#E11D48", text: "#881337" },
  { background: "#D1FAE5", border: "#059669", text: "#064E3B" },
  { background: "#E2E8F0", border: "#475569", text: "#1E293B" },
];

// Ekranda ham kalıp etiketi korunur: Q42.4 yine Q42.4 görünür.
const formatKalipLabel = (value) => {
  const normalized = String(value ?? "")
    .trim()
    .toLocaleUpperCase("tr-TR")
    .replace(/\s/g, "")
    .replace(/,/g, ".")
    .replace(/[×*]/g, "X");

  return normalized || "-";
};

// Analiz, renk ve seçim anahtarında ondalık kısım atılır.
// Q42.4 ve Q42 ekranda farklı yazılsa da ikisinin anahtarı Ø42 olur.
const normalizeKalipDimensionToken = (value) => {
  const normalized = String(value ?? "").trim().replace(",", ".");
  const numberValue = Number(normalized);

  return Number.isFinite(numberValue)
    ? String(Math.trunc(numberValue))
    : normalized;
};

const normalizeKalipKey = (value) => {
  const displayLabel = formatKalipLabel(value);

  if (!displayLabel || displayLabel === "-") {
    return "";
  }

  const hasRoundPrefix = /^[QØ]/.test(displayLabel);
  const body = displayLabel.replace(/^[QØ]/, "");
  const normalizedBody = body
    .split("X")
    .map(normalizeKalipDimensionToken)
    .join("X");

  return `${hasRoundPrefix ? "Ø" : ""}${normalizedBody}`;
};

const getDefaultKalipColor = (kalip) => {
  const key = normalizeKalipKey(kalip);
  let hash = 0;

  for (let index = 0; index < key.length; index += 1) {
    hash = (hash * 31 + key.charCodeAt(index)) >>> 0;
  }

  return KALIP_COLOR_PALETTE[hash % KALIP_COLOR_PALETTE.length];
};

const getUniqueKalipLabels = (values = []) => {
  const kalipMap = new Map();

  values.forEach((kalip) => {
    const key = normalizeKalipKey(kalip);

    if (key && key !== "-" && !kalipMap.has(key)) {
      kalipMap.set(key, formatKalipLabel(kalip));
    }
  });

  return Array.from(kalipMap.values());
};

const createAnalysisKalipColorMap = (analysisData = []) => {
  const kalipKeys = [];

  const addKalip = (kalip) => {
    const key = normalizeKalipKey(kalip);

    if (key && key !== "-" && !kalipKeys.includes(key)) {
      kalipKeys.push(key);
    }
  };

  (analysisData || []).forEach((month) => {
    (month?.kaliplar || []).forEach(addKalip);

    (month?.makineler || []).forEach((machine) => {
      (machine?.kaliplar || []).forEach(addKalip);
      (machine?.kalipKullanimlari || []).forEach((item) =>
        addKalip(item?.kalip)
      );
      (machine?.gecisOzeti || []).forEach((item) => {
        addKalip(item?.oncekiKalip);
        addKalip(item?.yeniKalip);
      });
      (machine?.gunler || []).forEach((day) => {
        (day?.hamKalipSirasi || []).forEach(addKalip);
        (day?.sadeKalipSirasi || []).forEach(addKalip);
      });
    });
  });

  return new Map(
    kalipKeys.map((key, index) => [
      key,
      KALIP_COLOR_PALETTE[index % KALIP_COLOR_PALETTE.length],
    ])
  );
};

const getIstanbulDateInput = (date = new Date()) => {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

const createInitialFilters = () => {
  const today = getIstanbulDateInput();

  return {
    startDate: `${today.slice(0, 7)}-01`,
    endDate: today,
    makinaAdi: "all",
    urunTipi: "all",
  };
};

const getAnalysisPayload = (response) => {
  const payload = response?.data ?? response ?? {};

  return {
    data: Array.isArray(payload?.data) ? payload.data : [],
    summary:
      payload?.summary && typeof payload.summary === "object"
        ? payload.summary
        : {},
    setupKurali:
      payload?.setupKurali && typeof payload.setupKurali === "object"
        ? payload.setupKurali
        : {},
  };
};

const formatNumber = (value, maximumFractionDigits = 2) => {
  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return "-";
  }

  return new Intl.NumberFormat("tr-TR", {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(numberValue);
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const [year, month, day] = String(value).slice(0, 10).split("-");

  if (!year || !month || !day) {
    return String(value);
  }

  return `${day}.${month}.${year}`;
};

const getMachineLabel = (machineNo) => {
  const parsed = Number(machineNo);

  return Number.isFinite(parsed) ? `M${parsed}` : String(machineNo ?? "-");
};

const getSetupTypeLabel = (setupTipi) => {
  switch (setupTipi) {
    case "GRUP_ICI":
      return "Grup İçi";
    case "GRUP_DISI":
      return "Grup Dışı";
    case "GRUP_BULUNAMADI":
      return "Grup Bulunamadı";
    case "AYNI_KALIP":
      return "Aynı Kalıp";
    default:
      return "-";
  }
};

const getSetupBadgeVariant = (setupTipi) => {
  switch (setupTipi) {
    case "GRUP_ICI":
      return "info";
    case "GRUP_DISI":
      return "warning";
    case "GRUP_BULUNAMADI":
      return "danger";
    default:
      return "secondary";
  }
};

function KalipBadge({
  kalip,
  selectedKalip = null,
  onSelectKalip,
  getColor = getDefaultKalipColor,
  className = "",
  title,
}) {
  const label = formatKalipLabel(kalip);
  const kalipKey = normalizeKalipKey(label);
  const selectedKey = selectedKalip
    ? normalizeKalipKey(selectedKalip)
    : null;
  const isSelected = Boolean(selectedKey && selectedKey === kalipKey);
  const isMuted = Boolean(selectedKey && !isSelected);
  const color = getColor(label);
  const Tag = onSelectKalip ? "button" : "span";

  return (
    <Tag
      {...(onSelectKalip
        ? {
            type: "button",
            onClick: () => onSelectKalip(label),
            "aria-pressed": isSelected,
          }
        : {})}
      className={`kalip-color-badge ${isSelected ? "is-selected" : ""} ${
        isMuted ? "is-muted" : ""
      } ${className}`.trim()}
      style={{
        "--kalip-bg": color.background,
        "--kalip-border": color.border,
        "--kalip-text": color.text,
      }}
      title={
        title ||
        (onSelectKalip
          ? isSelected
            ? `${label} seçimini kaldır`
            : `${label} kalıbını vurgula`
          : label)
      }
    >
      {label}
    </Tag>
  );
}

function SequenceBadges({
  sequence = [],
  selectedKalip = null,
  onSelectKalip,
  getColor = getDefaultKalipColor,
}) {
  if (!Array.isArray(sequence) || sequence.length === 0) {
    return <span className="text-muted">-</span>;
  }

  return (
    <div className="kalip-sequence">
      {sequence.map((kalip, index) => (
        <React.Fragment key={`${kalip}-${index}`}>
          {index > 0 && <span className="kalip-arrow">→</span>}
          <KalipBadge
            kalip={kalip}
            selectedKalip={selectedKalip}
            onSelectKalip={onSelectKalip}
            getColor={getColor}
            className="kalip-sequence-badge"
          />
        </React.Fragment>
      ))}
    </div>
  );
}

function SummaryCard({ label, value, detail, accent }) {
  return (
    <Card
      className="summary-card shadow-sm"
      style={{ "--summary-accent": accent }}
    >
      <Card.Body>
        <div className="summary-label">{label}</div>
        <div className="summary-value">{value}</div>
        <div className="summary-detail">{detail}</div>
      </Card.Body>
    </Card>
  );
}

function KalipKullanimTable({
  rows = [],
  selectedKalip = null,
  onSelectKalip,
  getColor = getDefaultKalipColor,
}) {
  const selectedKey = selectedKalip
    ? normalizeKalipKey(selectedKalip)
    : null;

  return (
    <div className="table-wrapper">
      <Table bordered hover responsive={false} className="analysis-table">
        <thead>
          <tr>
            <th>Kalıp</th>
            <th>Kalınlıklar</th>
            <th className="text-end">Üretim Günü</th>
            <th className="text-end">Kayıt</th>
            <th className="text-end">Toplam Kg</th>
            <th className="text-end">Ort. Hız</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row, index) => {
            const rowKey = normalizeKalipKey(row?.kalip);
            const isSelected = Boolean(selectedKey && selectedKey === rowKey);
            const isMuted = Boolean(selectedKey && !isSelected);
            const rowColor = getColor(row?.kalip);

            return (
              <tr
                key={`${row?.kalip}-${index}`}
                className={`${isSelected ? "kalip-row-selected" : ""} ${
                  isMuted ? "kalip-row-muted" : ""
                }`.trim()}
                style={{ "--kalip-row-bg": rowColor.background }}
              >
                <td>
                  <KalipBadge
                    kalip={row?.kalip}
                    selectedKalip={selectedKalip}
                    onSelectKalip={onSelectKalip}
                    getColor={getColor}
                  />
                </td>
                <td>
                  {(row?.kalinliklar || []).length > 0
                    ? (row.kalinliklar || [])
                        .map((value) => formatNumber(value, 3))
                        .join(" / ")
                    : "-"}
                </td>
                <td className="text-end">
                  {formatNumber(row?.uretimYapilanGunSayisi, 0)}
                </td>
                <td className="text-end">
                  {formatNumber(row?.uretimKaydiSayisi, 0)}
                </td>
                <td className="text-end fw-semibold">
                  {formatNumber(row?.toplamKg, 0)}
                </td>
                <td className="text-end">
                  {formatNumber(row?.ortalamaHiz, 2)} m/dk
                </td>
              </tr>
            );
          })}

          {rows.length === 0 && (
            <tr>
              <td colSpan={6} className="empty-state">
                Kalıp kullanım kaydı bulunamadı.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
}

function GecisOzetiTable({
  rows = [],
  selectedKalip = null,
  onSelectKalip,
  getColor = getDefaultKalipColor,
}) {
  const selectedKey = selectedKalip
    ? normalizeKalipKey(selectedKalip)
    : null;

  return (
    <div className="table-wrapper">
      <Table bordered hover responsive={false} className="analysis-table">
        <thead>
          <tr>
            <th>Geçiş</th>
            <th>Tip</th>
            <th className="text-end">Adet</th>
            <th className="text-end">Kalıp Değişim</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row, index) => {
            const transitionIncludesSelection = Boolean(
              selectedKey &&
                [row?.oncekiKalip, row?.yeniKalip].some(
                  (kalip) => normalizeKalipKey(kalip) === selectedKey
                )
            );

            return (
              <tr
                key={`${row?.oncekiKalip}-${row?.yeniKalip}-${row?.setupTipi}-${index}`}
                className={
                  selectedKey && !transitionIncludesSelection
                    ? "kalip-row-muted"
                    : transitionIncludesSelection
                    ? "kalip-transition-selected"
                    : ""
                }
              >
                <td>
                  <div className="transition-kalip-pair">
                    <KalipBadge
                      kalip={row?.oncekiKalip}
                      selectedKalip={selectedKalip}
                      onSelectKalip={onSelectKalip}
                      getColor={getColor}
                    />
                    <span className="kalip-arrow">→</span>
                    <KalipBadge
                      kalip={row?.yeniKalip}
                      selectedKalip={selectedKalip}
                      onSelectKalip={onSelectKalip}
                      getColor={getColor}
                    />
                  </div>
                </td>
                <td>
                  <Badge
                    bg={getSetupBadgeVariant(row?.setupTipi)}
                    text={row?.setupTipi === "GRUP_ICI" ? "dark" : undefined}
                  >
                    {getSetupTypeLabel(row?.setupTipi)}
                  </Badge>
                </td>
                <td className="text-end fw-bold">
                  {formatNumber(row?.gecisSayisi, 0)}
                </td>
                <td className="text-end fw-semibold">
                  {formatNumber(row?.toplamStandartSetupDakika, 0)} dk
                </td>
              </tr>
            );
          })}

          {rows.length === 0 && (
            <tr>
              <td colSpan={4} className="empty-state">
                Farklı kalıplar arasında geçiş bulunamadı.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
}

function GunlukSiraTable({
  rows = [],
  selectedKalip = null,
  onSelectKalip,
  getColor = getDefaultKalipColor,
}) {
  const selectedKey = selectedKalip
    ? normalizeKalipKey(selectedKalip)
    : null;

  return (
    <div className="table-wrapper">
      <Table
        bordered
        hover
        responsive={false}
        className="analysis-table daily-table"
      >
        <thead>
          <tr>
            <th>Tarih</th>
            <th>Gerçek Üretim Sırası</th>
            <th>Sade Kalıp Sırası</th>
            <th className="text-end">Farklı Kalıp</th>
            <th className="text-end">Geçiş</th>
            <th className="text-end">Kalıp Değişim</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((day, index) => {
            const dayContainsSelection = Boolean(
              selectedKey &&
                (day?.hamKalipSirasi || []).some(
                  (kalip) => normalizeKalipKey(kalip) === selectedKey
                )
            );

            return (
              <tr
                key={`${day?.tarih}-${index}`}
                className={
                  selectedKey && !dayContainsSelection
                    ? "kalip-row-muted"
                    : dayContainsSelection
                    ? "kalip-day-selected"
                    : ""
                }
              >
                <td className="fw-bold">{formatDate(day?.tarih)}</td>
                <td>
                  <SequenceBadges
                    sequence={day?.hamKalipSirasi || []}
                    selectedKalip={selectedKalip}
                    onSelectKalip={onSelectKalip}
                    getColor={getColor}
                  />
                </td>
                <td>
                  <SequenceBadges
                    sequence={day?.sadeKalipSirasi || []}
                    selectedKalip={selectedKalip}
                    onSelectKalip={onSelectKalip}
                    getColor={getColor}
                  />
                </td>
                <td className="text-end">
                  {formatNumber(day?.farkliKalipSayisi, 0)}
                </td>
                <td className="text-end fw-semibold">
                  {formatNumber(day?.oncekiGundenGelenGecisDahil, 0)}
                </td>
                <td className="text-end fw-bold">
                  {formatNumber(day?.toplamStandartSetupDakika, 0)} dk
                </td>
              </tr>
            );
          })}

          {rows.length === 0 && (
            <tr>
              <td colSpan={6} className="empty-state">
                Günlük üretim sırası bulunamadı.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
}

function MachineCard({ machine, getColor = getDefaultKalipColor }) {
  const [selectedKalip, setSelectedKalip] = useState(null);

  const handleKalipSelect = useCallback((kalip) => {
    setSelectedKalip((current) =>
      current && normalizeKalipKey(current) === normalizeKalipKey(kalip)
        ? null
        : formatKalipLabel(kalip)
    );
  }, []);

  useEffect(() => {
    if (
      selectedKalip &&
      !(machine?.kaliplar || []).some(
        (kalip) => normalizeKalipKey(kalip) === normalizeKalipKey(selectedKalip)
      )
    ) {
      setSelectedKalip(null);
    }
  }, [machine, selectedKalip]);

  return (
    <Card className="machine-card shadow-sm">
      <Card.Header className="machine-card-header">
        <div>
          <div className="machine-title">
            <span>{getMachineLabel(machine?.makinaAdi)}</span>
            <strong>Makine Kalıp Özeti</strong>
          </div>

          <div className="machine-kalip-list">
            {getUniqueKalipLabels(machine?.kaliplar || []).map((kalip) => (
              <KalipBadge
                key={normalizeKalipKey(kalip)}
                kalip={kalip}
                selectedKalip={selectedKalip}
                onSelectKalip={handleKalipSelect}
                getColor={getColor}
              />
            ))}
          </div>

          <div className="kalip-selection-help">
            {selectedKalip ? (
              <>
                <strong>{formatKalipLabel(selectedKalip)}</strong> seçili. Alt
                tablolarda yalnızca bu kalıp vurgulanıyor. Tekrar tıklayarak
                seçimi kaldırabilirsiniz.
              </>
            ) : (
              "Bir kalıba tıklayın; kullanım, geçiş ve günlük sıra tablolarında aynı kalıp vurgulansın."
            )}
          </div>
        </div>

        <div className="machine-metrics">
          <div>
            <small>Farklı Kalıp</small>
            <strong>{formatNumber(machine?.farkliKalipSayisi, 0)}</strong>
          </div>
          <div>
            <small>Geçiş</small>
            <strong>{formatNumber(machine?.toplamKalipGecisSayisi, 0)}</strong>
          </div>
          <div>
            <small>Grup İçi</small>
            <strong>{formatNumber(machine?.grupIciGecisSayisi, 0)}</strong>
          </div>
          <div>
            <small>Grup Dışı</small>
            <strong>{formatNumber(machine?.grupDisiGecisSayisi, 0)}</strong>
          </div>
          <div>
            <small>Setup</small>
            <strong>
              {formatNumber(machine?.toplamStandartSetupSaat, 2)} saat
            </strong>
          </div>
          <div>
            <small>Üretim</small>
            <strong>{formatNumber(machine?.toplamKg, 0)} kg</strong>
          </div>
        </div>
      </Card.Header>

      <Card.Body>
        <Row className="g-3">
          <Col xs={12} xl={7}>
            <div className="section-title">
              <h5>Kullanılan Kalıplar</h5>
              <small>Ay boyunca kullanılan kalıpların kısa özeti</small>
            </div>
            <KalipKullanimTable
              rows={machine?.kalipKullanimlari || []}
              selectedKalip={selectedKalip}
              onSelectKalip={handleKalipSelect}
              getColor={getColor}
            />
          </Col>

          <Col xs={12} xl={5}>
            <div className="section-title">
              <h5>Kalıp Geçişleri</h5>
              <small>Geçiş tipi, adedi ve Kalıp Değişim süresi</small>
            </div>
            <GecisOzetiTable
              rows={machine?.gecisOzeti || []}
              selectedKalip={selectedKalip}
              onSelectKalip={handleKalipSelect}
              getColor={getColor}
            />
          </Col>
        </Row>

        <div className="section-title mt-4">
          <h5>Günlük Üretim Sırası</h5>
          <small>
            Aynı gün makinede oluşan gerçek kalıp sırası doğrudan gösterilir
          </small>
        </div>
        <GunlukSiraTable
          rows={machine?.gunler || []}
          selectedKalip={selectedKalip}
          onSelectKalip={handleKalipSelect}
          getColor={getColor}
        />
      </Card.Body>
    </Card>
  );
}

function AylikKalipKullanimAnalizi() {
  const [filters, setFilters] = useState(createInitialFilters);
  const [appliedFilters, setAppliedFilters] = useState(createInitialFilters);
  const [analysisData, setAnalysisData] = useState([]);
  const [summary, setSummary] = useState({});
  const [setupKurali, setSetupKurali] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const fetchAnalysis = useCallback(async (requestFilters) => {
    try {
      setLoading(true);
      setErrorMessage("");

      if (!requestFilters.startDate || !requestFilters.endDate) {
        setErrorMessage("Başlangıç ve bitiş tarihini seçmelisiniz.");
        return;
      }

      if (requestFilters.startDate > requestFilters.endDate) {
        setErrorMessage("Başlangıç tarihi bitiş tarihinden sonra olamaz.");
        return;
      }

      const response = await getAylikKalipKullanimAnalizi({
        startDate: requestFilters.startDate,
        endDate: requestFilters.endDate,
        makinaAdi: requestFilters.makinaAdi,
        urunTipi: requestFilters.urunTipi,
      });

      const payload = getAnalysisPayload(response);

      setAnalysisData(payload.data);
      setSummary(payload.summary);
      setSetupKurali(payload.setupKurali);
      setAppliedFilters({ ...requestFilters });
      setHasSearched(true);
    } catch (error) {
      console.error(
        "Aylık kalıp kullanım analizi alınırken hata oluştu:",
        error
      );

      setAnalysisData([]);
      setSummary({});
      setSetupKurali({});
      setHasSearched(true);
      setErrorMessage(
        error?.response?.data?.message ||
          "Aylık kalıp kullanım analizi alınamadı."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const initialFilters = createInitialFilters();
    fetchAnalysis(initialFilters);
  }, [fetchAnalysis]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    fetchAnalysis(filters);
  };

  const handleReset = () => {
    const initialFilters = createInitialFilters();
    setFilters(initialFilters);
    fetchAnalysis(initialFilters);
  };

  const visibleMachineCount = useMemo(() => {
    return (analysisData || []).reduce(
      (total, month) => total + (month?.makineler || []).length,
      0
    );
  }, [analysisData]);

  const analysisKalipColorMap = useMemo(
    () => createAnalysisKalipColorMap(analysisData),
    [analysisData]
  );

  const getAnalysisKalipColor = useCallback(
    (kalip) =>
      analysisKalipColorMap.get(normalizeKalipKey(kalip)) ||
      getDefaultKalipColor(kalip),
    [analysisKalipColorMap]
  );

  const selectedMachineLabel =
    MACHINE_OPTIONS.find(
      (item) => item.value === String(appliedFilters.makinaAdi)
    )?.label || "Tüm Makineler";

  const selectedProductTypeLabel =
    PRODUCT_TYPE_OPTIONS.find((item) => item.value === appliedFilters.urunTipi)
      ?.label || "Tüm Ürün Tipleri";

  return (
    <Layout>
      <style>{`
        .mold-analysis-page {
          padding-bottom: 28px;
        }

        .page-heading {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 18px;
        }

        .page-heading h3 {
          margin: 0;
          font-weight: 850;
        }

        .page-heading p {
          margin: 5px 0 0;
          color: var(--bs-secondary-color);
          font-size: 0.9rem;
        }

        .filter-card,
        .summary-card,
        .month-card,
        .machine-card {
          border: 1px solid var(--bs-border-color);
          border-radius: 14px;
          background: var(--bs-body-bg);
          color: var(--bs-body-color);
        }

        .filter-card {
          margin-bottom: 18px;
        }

        .filter-label {
          margin-bottom: 6px;
          font-size: 0.8rem;
          font-weight: 750;
        }

        .active-filters {
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
          margin-top: 12px;
        }

        .setup-note {
          margin-top: 12px;
          padding: 10px 12px;
          border: 1px dashed var(--bs-warning);
          border-radius: 9px;
          background: rgba(255, 193, 7, 0.08);
          font-size: 0.8rem;
        }

        .summary-card {
          height: 100%;
          position: relative;
          overflow: hidden;
        }

        .summary-card::before {
          content: "";
          position: absolute;
          inset: 0 auto 0 0;
          width: 5px;
          background: var(--summary-accent);
        }

        .summary-card .card-body {
          padding-left: 20px;
        }

        .summary-label {
          color: var(--bs-secondary-color);
          font-size: 0.78rem;
          font-weight: 750;
        }

        .summary-value {
          margin-top: 5px;
          font-size: 1.45rem;
          font-weight: 850;
          line-height: 1.1;
        }

        .summary-detail {
          margin-top: 7px;
          color: var(--bs-secondary-color);
          font-size: 0.72rem;
        }

        .month-card {
          overflow: hidden;
          margin-bottom: 22px;
        }

        .month-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 16px 18px;
          border-bottom: 1px solid var(--bs-border-color);
          background: var(--bs-secondary-bg);
        }

        .month-header h4 {
          margin: 0;
          font-size: 1.12rem;
          font-weight: 850;
        }

        .month-header small {
          color: var(--bs-secondary-color);
        }

        .month-metrics {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .month-metric {
          min-width: 86px;
          padding: 7px 9px;
          border: 1px solid var(--bs-border-color);
          border-radius: 8px;
          background: var(--bs-body-bg);
          text-align: center;
        }

        .month-metric small,
        .month-metric strong {
          display: block;
        }

        .month-metric small {
          color: var(--bs-secondary-color);
          font-size: 0.66rem;
          font-weight: 700;
        }

        .month-metric strong {
          margin-top: 2px;
          font-size: 0.9rem;
        }

        .month-kalip-list {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          padding: 12px 18px;
          border-bottom: 1px solid var(--bs-border-color);
        }

        .machine-list {
          display: grid;
          gap: 16px;
          padding: 16px;
        }

        .machine-card {
          overflow: hidden;
          margin: 0;
        }

        .machine-card-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          padding: 14px 16px;
          background: var(--bs-body-bg);
        }

        .machine-title {
          display: flex;
          align-items: center;
          gap: 9px;
        }

        .machine-title span {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 46px;
          height: 36px;
          border-radius: 9px;
          background: var(--bs-primary);
          color: white;
          font-weight: 850;
        }

        .machine-title strong {
          font-size: 1rem;
        }

        .machine-kalip-list {
          display: flex;
          flex-wrap: wrap;
          gap: 5px;
          margin-top: 8px;
        }

        .kalip-color-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 25px;
          padding: 4px 9px;
          border: 1px solid var(--kalip-border);
          border-radius: 999px;
          background: var(--kalip-bg);
          color: var(--kalip-text);
          font: inherit;
          font-size: 0.72rem;
          font-weight: 800;
          line-height: 1;
          white-space: nowrap;
          transition: opacity 0.15s ease, transform 0.15s ease,
            box-shadow 0.15s ease, background 0.15s ease;
        }

        button.kalip-color-badge {
          cursor: pointer;
        }

        button.kalip-color-badge:hover {
          transform: translateY(-1px);
          box-shadow: 0 3px 8px rgba(15, 23, 42, 0.16);
        }

        .kalip-color-badge.is-selected {
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--kalip-border) 30%, transparent);
          transform: translateY(-1px);
        }

        .kalip-color-badge.is-muted {
          border-color: var(--bs-border-color);
          background: var(--bs-secondary-bg);
          color: var(--bs-secondary-color);
          opacity: 0.34;
          box-shadow: none;
          transform: none;
        }

        .kalip-selection-help {
          max-width: 620px;
          margin-top: 8px;
          color: var(--bs-secondary-color);
          font-size: 0.7rem;
        }

        .kalip-selection-help strong {
          color: var(--bs-body-color);
        }

        .transition-kalip-pair {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .machine-metrics {
          display: flex;
          flex-wrap: wrap;
          justify-content: flex-end;
          gap: 8px;
        }

        .machine-metrics > div {
          min-width: 72px;
          padding: 7px 9px;
          border: 1px solid var(--bs-border-color);
          border-radius: 8px;
          text-align: center;
        }

        .machine-metrics small,
        .machine-metrics strong {
          display: block;
        }

        .machine-metrics small {
          color: var(--bs-secondary-color);
          font-size: 0.64rem;
          font-weight: 700;
        }

        .machine-metrics strong {
          margin-top: 2px;
          font-size: 0.86rem;
        }

        .section-title {
          margin-bottom: 9px;
        }

        .section-title h5 {
          margin: 0;
          font-size: 0.95rem;
          font-weight: 850;
        }

        .section-title small {
          color: var(--bs-secondary-color);
          font-size: 0.73rem;
        }

        .table-wrapper {
          width: 100%;
          overflow-x: auto;
          border: 1px solid var(--bs-border-color);
          border-radius: 9px;
        }

        .analysis-table {
          min-width: 660px;
          margin: 0;
          vertical-align: middle;
        }

        .daily-table {
          min-width: 980px;
        }

        .analysis-table thead th {
          padding: 10px;
          border-color: #3b4858;
          background: #293544;
          color: #ffffff;
          font-size: 0.73rem;
          font-weight: 750;
          white-space: nowrap;
        }

        .analysis-table tbody td {
          padding: 9px 10px;
          font-size: 0.78rem;
          white-space: nowrap;
        }

        .analysis-table tbody tr:hover td {
          background: rgba(13, 110, 253, 0.05);
        }

        .analysis-table tbody tr.kalip-row-selected td {
          background: var(--kalip-row-bg) !important;
        }

        .analysis-table tbody tr.kalip-transition-selected td,
        .analysis-table tbody tr.kalip-day-selected td {
          background: rgba(13, 110, 253, 0.055);
        }

        .analysis-table tbody tr.kalip-row-muted {
          opacity: 0.45;
        }

        .analysis-table tbody tr.kalip-row-muted:hover td {
          background: transparent;
        }

        .kalip-sequence {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 5px;
          min-width: 260px;
        }

        .kalip-arrow {
          color: var(--bs-secondary-color);
          font-weight: 800;
        }

        .kalip-sequence-badge {
          font-size: 0.72rem;
        }

        .empty-state {
          padding: 32px 16px !important;
          color: var(--bs-secondary-color) !important;
          text-align: center;
        }

        @media (max-width: 992px) {
          .page-heading,
          .month-header,
          .machine-card-header {
            flex-direction: column;
          }

          .machine-metrics {
            justify-content: flex-start;
          }
        }

        @media (max-width: 768px) {
          .page-heading h3 {
            font-size: 1.3rem;
          }

          .summary-value {
            font-size: 1.2rem;
          }

          .analysis-table thead th,
          .analysis-table tbody td {
            padding: 8px;
            font-size: 0.7rem;
          }
        }
      `}</style>

      <div className="mold-analysis-page">
        <div className="page-heading">
          <div>
            <h3>Aylık Kalıp Kullanım Analizi</h3>
            <p>
              İlgili ayda kullanılan kalıpları, makine bazlı gerçek üretim
              sırasını, kalıp geçişlerini ve Kalıp Değişim sürelerini tek
              ekranda görüntüleyin.
            </p>
          </div>

          <Badge bg="primary" className="px-3 py-2">
            {visibleMachineCount} makine
          </Badge>
        </div>

        <Card className="filter-card shadow-sm">
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Row className="g-3 align-items-end">
                <Col xs={12} md={6} lg={2}>
                  <Form.Group>
                    <Form.Label className="filter-label">
                      Başlangıç Tarihi
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      value={filters.startDate}
                      max={filters.endDate}
                      onChange={handleFilterChange}
                    />
                  </Form.Group>
                </Col>

                <Col xs={12} md={6} lg={2}>
                  <Form.Group>
                    <Form.Label className="filter-label">
                      Bitiş Tarihi
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="endDate"
                      value={filters.endDate}
                      min={filters.startDate}
                      max={getIstanbulDateInput()}
                      onChange={handleFilterChange}
                    />
                  </Form.Group>
                </Col>

                <Col xs={12} md={6} lg={3}>
                  <Form.Group>
                    <Form.Label className="filter-label">Makine</Form.Label>
                    <Form.Select
                      name="makinaAdi"
                      value={filters.makinaAdi}
                      onChange={handleFilterChange}
                    >
                      {MACHINE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col xs={12} md={6} lg={3}>
                  <Form.Group>
                    <Form.Label className="filter-label">Ürün Tipi</Form.Label>
                    <Form.Select
                      name="urunTipi"
                      value={filters.urunTipi}
                      onChange={handleFilterChange}
                    >
                      {PRODUCT_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col xs={12} lg={2}>
                  <div className="d-grid gap-2">
                    <Button type="submit" variant="primary" disabled={loading}>
                      {loading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Hesaplanıyor
                        </>
                      ) : (
                        "Analiz Et"
                      )}
                    </Button>

                    <Button
                      type="button"
                      variant="outline-secondary"
                      disabled={loading}
                      onClick={handleReset}
                    >
                      Sıfırla
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>

            <div className="active-filters">
              <Badge bg="secondary">
                {formatDate(appliedFilters.startDate)} /{" "}
                {formatDate(appliedFilters.endDate)}
              </Badge>
              <Badge bg="info" text="dark">
                {selectedMachineLabel}
              </Badge>
              <Badge bg="dark">{selectedProductTypeLabel}</Badge>
            </div>

            <div className="setup-note">
              <strong>Kalıp Değişim kuralı:</strong> aynı grup{" "}
              {formatNumber(setupKurali?.ayniGrupDakika ?? 90, 0)} dakika,
              farklı grup{" "}
              {formatNumber(setupKurali?.farkliGrupDakika ?? 240, 0)} dakika. Kalıp değişimi Excel dosyasından alındı.
            </div>
          </Card.Body>
        </Card>

        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

        <Row className="g-3 mb-4">
          <Col xs={6} lg={3}>
            <SummaryCard
              label="Farklı Kalıp"
              value={formatNumber(summary?.donemBoyuncaFarkliKalipSayisi, 0)}
              detail="Seçilen dönemde kullanılan benzersiz kalıplar"
              accent="#20c997"
            />
          </Col>

          <Col xs={6} lg={3}>
            <SummaryCard
              label="Kalıp Geçişi"
              value={formatNumber(summary?.toplamKalipGecisSayisi, 0)}
              detail={`${formatNumber(
                summary?.grupIciGecisSayisi,
                0
              )} grup içi · ${formatNumber(
                summary?.grupDisiGecisSayisi,
                0
              )} grup dışı`}
              accent="#ffc107"
            />
          </Col>

          <Col xs={6} lg={3}>
            <SummaryCard
              label="Kalıp Değişim"
              value={`${formatNumber(
                summary?.toplamStandartSetupSaat,
                2
              )} saat`}
              detail={`${formatNumber(
                summary?.toplamStandartSetupDakika,
                0
              )} dakika toplam standart süre`}
              accent="#dc3545"
            />
          </Col>

          <Col xs={6} lg={3}>
            <SummaryCard
              label="Toplam Üretim"
              value={`${formatNumber(summary?.toplamKg, 0)} kg`}
              detail={`${formatNumber(
                summary?.toplamKayitSayisi,
                0
              )} üretim kaydı`}
              accent="#198754"
            />
          </Col>
        </Row>

        {loading && analysisData.length === 0 ? (
          <Card className="month-card shadow-sm">
            <Card.Body className="empty-state">
              <Spinner animation="border" className="mb-3" />
              <div>Kalıp kullanım analizi hazırlanıyor...</div>
            </Card.Body>
          </Card>
        ) : (
          (analysisData || []).map((month) => (
            <Card key={month?.ayKey} className="month-card shadow-sm">
              <div className="month-header">
                <div>
                  <h4>
                    {month?.ayAdi} {month?.yil}
                  </h4>
                  <small>
                    Ay içinde kullanılan tüm kalıplar ve makine sıraları
                  </small>
                </div>

                <div className="month-metrics">
                  <div className="month-metric">
                    <small>Makine</small>
                    <strong>
                      {formatNumber(month?.toplamMakinaSayisi, 0)}
                    </strong>
                  </div>
                  <div className="month-metric">
                    <small>Farklı Kalıp</small>
                    <strong>{formatNumber(month?.farkliKalipSayisi, 0)}</strong>
                  </div>
                  <div className="month-metric">
                    <small>Geçiş</small>
                    <strong>
                      {formatNumber(month?.toplamKalipGecisSayisi, 0)}
                    </strong>
                  </div>
                  <div className="month-metric">
                    <small>Setup</small>
                    <strong>
                      {formatNumber(month?.toplamStandartSetupSaat, 2)} saat
                    </strong>
                  </div>
                  <div className="month-metric">
                    <small>Üretim</small>
                    <strong>{formatNumber(month?.toplamKg, 0)} kg</strong>
                  </div>
                </div>
              </div>

              <div className="month-kalip-list">
                {getUniqueKalipLabels(month?.kaliplar || []).map((kalip) => (
                  <KalipBadge
                    key={normalizeKalipKey(kalip)}
                    kalip={kalip}
                    getColor={getAnalysisKalipColor}
                  />
                ))}
              </div>

              <div className="machine-list">
                {(month?.makineler || []).map((machine, machineIndex) => (
                  <MachineCard
                    key={`${month?.ayKey}-${machine?.makinaAdi}-${machineIndex}`}
                    machine={machine}
                    getColor={getAnalysisKalipColor}
                  />
                ))}

                {(month?.makineler || []).length === 0 && (
                  <div className="empty-state">Makine kaydı bulunamadı.</div>
                )}
              </div>
            </Card>
          ))
        )}

        {!loading && hasSearched && analysisData.length === 0 && (
          <Card className="month-card shadow-sm">
            <Card.Body className="empty-state">
              Seçilen filtrelere uygun kalıp kullanım kaydı bulunamadı.
            </Card.Body>
          </Card>
        )}
      </div>
    </Layout>
  );
}

export default AylikKalipKullanimAnalizi;
